import { SLUG_MAX_LENGTH } from "./limits.js";
/**
 * The set of indexable pages, and the single source of truth for page keys.
 *
 * Both the admin SEO screen and the Astro build derive their page list from
 * here, so a page can never exist in one without metadata in the other. Article
 * pages are dynamic and use the "article:<id>" key form instead of this list.
 */
export const STATIC_PAGE_KEYS = [
    "home",
    "faq",
    "how-it-works",
    "top-up",
    "compatible-devices",
    "articles",
    "contact",
    "privacy",
    "terms",
    "404",
];
/** Pages that must never be indexed regardless of admin settings. */
export const ALWAYS_NOINDEX = ["404"];
/** Default slug per static page key. Admin can override per locale. */
export const DEFAULT_SLUGS = {
    home: "",
    faq: "faq",
    "how-it-works": "how-it-works",
    "top-up": "top-up",
    "compatible-devices": "compatible-devices",
    articles: "articles",
    contact: "contact",
    privacy: "privacy",
    terms: "terms",
    "404": "404",
};
export const ROBOTS_VALUES = ["index,follow", "noindex,nofollow"];
/**
 * Slugs are path segments, not paths: no slashes, no dots, no uppercase, no
 * doubled or edge dashes, so a slug change can never rewrite the locale prefix,
 * escape the site root, or produce a directory the static build refuses.
 *
 * The same rule is a CHECK on `seo_pages.slug`, `slug_history.old_slug` and
 * `article_i18n.slug`. SQL is the enforcer; this is the friendly error, and
 * `worker/test/schema/slugs.test.ts` fails if the two drift apart.
 */
export const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
/**
 * The empty slug is the site root and belongs to exactly one page, and no other
 * page may take it. The same two-branch rule is the CHECK on `seo_pages.slug`.
 */
export function isValidSlug(slug, pageKey) {
    if (pageKey === "home")
        return slug === "";
    if (slug === "")
        return false;
    return SLUG_RE.test(slug) && slug.length <= SLUG_MAX_LENGTH;
}
export function isArticleKey(key) {
    return key.startsWith("article:") && key.length > "article:".length;
}
/**
 * Is this string one of the page keys this vocabulary knows?
 *
 * Needed because `seo_pages.page_key` is TEXT and nothing narrows it on the way
 * out: a row written for a page later dropped from `STATIC_PAGE_KEYS` still
 * reads back. So the admin panel used to declare the key it SENDS as
 * `PageKey | string`, which TypeScript collapses to `string` -- a union with a
 * string in it constrains nothing, and reading like a constraint is worse than
 * saying `string` outright. This is the narrowing that union was pretending to
 * be, and it turns a key the worker would 400 on into a refusal before the
 * round trip.
 */
export function isPageKey(key) {
    return STATIC_PAGE_KEYS.includes(key) || isArticleKey(key);
}
/** The same, for the two values `seo_pages.robots` is allowed to hold. */
export function isRobotsValue(value) {
    return ROBOTS_VALUES.includes(value);
}
/**
 * Pages the slug route does NOT build, and why each one.
 *
 * Stated as an EXCLUSION so that building is the default and not building is
 * the thing somebody has to say out loud. Listed positively, the subset was a
 * third copy of the page list inside a hundred lines, and dropping a key from
 * it was silent: the page simply stopped being built, and `pages.spec.ts` --
 * which derives its list from here -- stopped testing it in the same breath.
 */
const OWN_ROUTE = [
    // The shop, with the buy panel on it.
    "home",
    // At the site root rather than under a locale, because Apache's
    // ErrorDocument points at exactly one file.
    "404",
];
const NOT_RENDERED = [
    // Nothing to list yet. An index page with no entries is thin content asking
    // to be indexed; it joins the set when there is an article.
    "articles",
];
/** Rendered by a route of its own, or not rendered at all. */
const NOT_SLUG_ROUTED = [...OWN_ROUTE, ...NOT_RENDERED];
/**
 * The page keys the storefront builds a body for.
 *
 * Here rather than in the site because three things need it and only one of
 * them is the build: the Astro route builds from it, the footer filters its
 * links by it, and the admin's SEO screen (M5) has to say "this page is not
 * built yet, so renaming its slug does nothing".
 *
 * The ORDER is `STATIC_PAGE_KEYS`' order, and it is DISPLAY order -- the
 * footer's link list is rendered from it. Frozen, because a caller sorting it
 * in place would reorder the footer of every page.
 */
export const BUILT_PAGE_KEYS = Object.freeze(STATIC_PAGE_KEYS.filter((key) => !NOT_SLUG_ROUTED.includes(key)));
/**
 * Does the storefront render a body for this page key?
 *
 * The comment above `BUILT_PAGE_KEYS` has always said the admin needs this --
 * "this page is not built yet, so renaming its slug does nothing" -- and the
 * rule was never applied to the one place it was load-bearing. A rename of an
 * unbuilt page writes a `slug_history` row, `readSeoSnapshot` hands it to the
 * build, `gen-htaccess.mjs` emits `Redirect 301 /en/old/ /en/new/`, and
 * `check-redirects.mjs` refuses it because `/en/new/` is not in `dist`. One
 * typo fixed in a draft article's URL then blocks EVERY storefront deploy,
 * including an urgent price change that has nothing to do with it.
 *
 * `article:<id>` is false for every id, because no article route exists yet.
 * When one does, this function is the single place that changes -- which is
 * why the answer is computed rather than written down twice.
 *
 * Takes a `string`, not a `PageKey`: the callers hold a value read out of a
 * database column, and a signature that demanded the narrow type would only
 * move the cast to the caller.
 */
export function isBuiltPageKey(key) {
    return BUILT_PAGE_KEYS.includes(key);
}
/**
 * The language the deployed storefront is built in.
 *
 * `pull-content.mjs` reads this locale's page metadata by default, and the
 * worker rebuilds the site only for an SEO save in it. `PULL_LOCALE` can
 * override the pull for a local build; the deploy workflow does not set it.
 */
export const BUILD_LOCALE = "en";
/**
 * Does the storefront render a page for this key, by any route?
 *
 * The slug-routed pages plus `OWN_ROUTE`. Not `articles` and not
 * `article:<id>`: their metadata is stored and nothing renders it yet.
 */
export function rendersPage(key) {
    return isBuiltPageKey(key) || OWN_ROUTE.includes(key);
}
/**
 * Does saving this SEO row change a page the deployed build renders?
 *
 * A row in another language, or for a page nothing renders, changes nothing
 * the build reads; rebuilding for it would be a build that does nothing and a
 * "the site is rebuilding" that is untrue.
 */
export function isSeoRowBuilt(pageKey, locale) {
    return locale === BUILD_LOCALE && rendersPage(pageKey);
}
/**
 * The path a SLUG becomes: "/" for the empty one, "/faq/" otherwise.
 *
 * The one implementation. It had four spellings -- the Astro build, the
 * htaccess generator, the redirect gate and a helper here -- and the admin's
 * SEO screen will need a fifth to show an operator what URL the page they are
 * renaming will have. That screen edits the OPERATOR's slug, which is exactly
 * why it cannot use a helper that takes a page key and reads the defaults.
 */
export function pathFromSlug(slug) {
    return slug === "" ? "/" : `/${slug}/`;
}
/**
 * The path a page has before any operator has renamed it.
 *
 * For callers holding a key and no snapshot -- a test running against a build
 * with no API reachable, where the defaults ARE what was built. Anything
 * holding a real snapshot passes its slug to `pathFromSlug` instead.
 */
export function defaultPagePath(pageKey) {
    return pathFromSlug(DEFAULT_SLUGS[pageKey]);
}
/**
 * The path a page has under a locale: `/en/`, `/en/faq/`.
 *
 * The outer half of `pathFromSlug`, and the one M5 needs: the admin's SEO
 * screen has to show an operator the URL their rename produces, and that is
 * this string rather than the bare path. It was spelled out at nine call sites
 * in the storefront before it had a name.
 */
export function localePath(locale, slug) {
    return `/${locale}${pathFromSlug(slug)}`;
}
/**
 * May this page be indexed?
 *
 * The RULE, not the data: the operator's `robots` column and `ALWAYS_NOINDEX`,
 * with the stricter winning. It lived only in the storefront, which is
 * Vite-only, so the admin would have re-derived it -- and an SEO screen
 * offering a `robots` dropdown for the 404 that the site silently ignores is
 * worse than one that greys it out.
 */
export function isNoindexPage(pageKey, robots) {
    if (ALWAYS_NOINDEX.includes(pageKey))
        return true;
    return robots === "noindex,nofollow";
}
/**
 * Is this page advertised to crawlers -- in the sitemap and in llms.txt?
 *
 * A page the build renders and nobody asked to keep out of the index. One
 * rule for both lists, so a page an operator sets to noindex leaves both at
 * once rather than one of them.
 */
export function isListedPage(pageKey, robots) {
    return rendersPage(pageKey) && !isNoindexPage(pageKey, robots);
}
//# sourceMappingURL=seo-pages.js.map